import React, { Component, createRef } from 'react'
import PropTypes from 'prop-types'
import _, { noop } from 'lodash'
import Slider from 'react-slick'
import classnames from 'classnames/bind'
import Icon from '../../elementals/icon'
import { CAROUSEL_DEFAULT_SPEED_MS } from '../../../constants/statics'
import styles from './styles.scss'
const cx = classnames.bind(styles)
const elmId = 'carousel'

const Arrow = ({
  isBackArrow,
  move,
  onClick,
  className = '',
  alwaysShowArrows,
  alwaysHideArrows,
  customClassName = '',
  arrowIconProps = {},
}) => {
  let iconName = isBackArrow ? arrowIconProps.backName : arrowIconProps.nextName
  iconName = iconName || isBackArrow ? 'angle-left' : 'angle-right'

  const arrowElmId = arrowIconProps.elmId || elmId
  return (
    !alwaysHideArrows && (
      <Icon
        iconStyle='regular'
        name={iconName}
        color='white'
        size='huge'
        fitted
        data-elm-id={
          isBackArrow
            ? `${arrowElmId}_carousel_back_arrow`
            : `${arrowElmId}_carousel_next_arrow`
        }
        className={cx(
          isBackArrow ? 'back-arrow' : 'next-arrow',
          {
            disabled: className.includes('slick-disabled') && !alwaysShowArrows,
          },
          customClassName
        )}
        onClick={event => {
          move(event)
          onClick && onClick(event)
        }}
        {..._.omit(arrowIconProps, 'backName', 'nextName', 'elmId')}
      />
    )
  )
}

const arrowPropTypes = {
  onClick: PropTypes.func,
  className: PropTypes.string,
  alwaysShowArrows: PropTypes.bool,
  alwaysHideArrows: PropTypes.bool,
  customClassName: PropTypes.string,
  arrowIconProps: PropTypes.object,
}

Arrow.propTypes = {
  isBackArrow: PropTypes.bool,
  move: PropTypes.func,
  ...arrowPropTypes,
}

export const NextArrow = ({ next, ...props }) => (
  <Arrow {...props} isBackArrow={false} move={next} />
)

NextArrow.propTypes = {
  ...arrowPropTypes,
  next: PropTypes.func,
}

export const BackArrow = ({ back, ...props }) => (
  <Arrow {...props} isBackArrow move={back} />
)

BackArrow.propTypes = {
  ...arrowPropTypes,
  back: PropTypes.func,
}

export default class Carousel extends Component {
  static propTypes = {
    children: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.node),
    ]).isRequired,
    alwaysShowArrows: PropTypes.bool,
    alwaysHideArrows: PropTypes.bool,
    settings: PropTypes.object,
    arrowIconProps: PropTypes.object,
    elmId: PropTypes.string,
    next: PropTypes.func,
    back: PropTypes.func,
    carouselRef: PropTypes.object,
    className: PropTypes.string,
  }

  static defaultProps = {
    alwaysShowArrows: false, // Accepting naming recommendations
    alwaysHideArrows: false,
    settings: {},
    arrowIconProps: {},
    next: noop,
    back: noop,
  }

  constructor(props) {
    super(props)
    if (props.carouselRef) {
      this.sliderRef = props.carouselRef
    } else {
      this.sliderRef = createRef()
    }
    this.handleKeyDown = this.handleKeyDown.bind(this)
  }

  /**
   * In a perfect world, we'd make the current slide a prop and update it when the slide changes, but that would
   * require a lot of changes to the way the carousel is used. Instead, we'll just expose a goToSlide method that
   * can be called from the parent component via a ref.
   */
  goToSlide = index => {
    if (this.sliderRef.current) {
      this.sliderRef.current.slickGoTo(index)
    }
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown)
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown)
  }

  handleKeyDown(event) {
    if (!this.sliderRef.current) return

    if (event.key === 'ArrowRight') {
      this.sliderRef.current.slickNext()
    } else if (event.key === 'ArrowLeft') {
      this.sliderRef.current.slickPrev()
    }
  }

  render() {
    const {
      children,
      settings,
      next,
      back,
      alwaysShowArrows,
      alwaysHideArrows,
      arrowIconProps,
      elmId,
      ...props
    } = this.props

    const defaultSettings = {
      speed: CAROUSEL_DEFAULT_SPEED_MS,
      slidesToShow: 1,
      slidesToScroll: 1,
      nextArrow: (
        <NextArrow
          next={next}
          alwaysShowArrows={alwaysShowArrows}
          alwaysHideArrows={alwaysHideArrows}
          arrowIconProps={arrowIconProps}
        />
      ),
      prevArrow: (
        <BackArrow
          back={back}
          alwaysShowArrows={alwaysShowArrows}
          alwaysHideArrows={alwaysHideArrows}
          arrowIconProps={arrowIconProps}
        />
      ),
    }

    const finalSettings = Object.assign({}, defaultSettings, settings)

    return (
      <div data-elm-id={`${elmId}_carousel`} className={props.className}>
        {Slider && (
          <Slider ref={this.sliderRef} {...finalSettings} {...props}>
            {children}
          </Slider>
        )}
      </div>
    )
  }
}
